import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  makeStyles,
  Paper,
  Grid,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  Select,
  Typography,
  Chip,
  Box,
  Tooltip,
  useMediaQuery,
  useTheme,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import ButtonWithSpinner from "../ButtonWithSpinner";

import {
  Edit as EditIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Search as SearchIcon,
} from "@material-ui/icons";

import { toast } from "react-toastify";
import useCompanies from "../../hooks/useCompanies";
import usePlans from "../../hooks/usePlans";
import ModalUsers from "../ModalUsers";
import api from "../../services/api";
import { head, isArray, has } from "lodash";
import { useDate } from "../../hooks/useDate";
import toastError from "../../errors/toastError";

import moment from "moment";

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";

  const chipBase = {
    fontWeight: 600,
    fontSize: "0.72rem",
    height: 22,
    "& .MuiChip-icon": { color: "inherit" },
  };

  return {
    root: {
      width: "100%",
    },
    mainPaper: {
      width: "100%",
      flex: 1,
      padding: theme.spacing(2),
    },
    fullWidth: {
      width: "100%",
    },
    tableContainer: {
      width: "100%",
      overflowX: "auto",
      ...theme.scrollbarStyles,
    },

    // Cabeçalho da tabela
    tableHead: {
      "& th": {
        fontWeight: 700,
        fontSize: "0.75rem",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.background.default,
        borderBottom: `2px solid ${theme.palette.divider}`,
        whiteSpace: "nowrap",
      },
    },
    tableRow: {
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
      transition: "background-color 0.15s ease",
    },
    tableRowSelected: {
      backgroundColor: isDark
        ? "rgba(45, 123, 232, 0.16)"
        : "rgba(45, 123, 232, 0.08)",
      boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
    },

    // Chips de status, com cores adaptadas ao tema claro/escuro
    chipAtivo: {
      ...chipBase,
      backgroundColor: isDark ? "rgba(76, 175, 80, 0.18)" : "#e8f5e9",
      color: isDark ? "#81c784" : "#2e7d32",
    },
    chipInativo: {
      ...chipBase,
      backgroundColor: isDark ? "rgba(244, 67, 54, 0.18)" : "#ffebee",
      color: isDark ? "#e57373" : "#c62828",
    },
    chipCampanha: {
      ...chipBase,
      backgroundColor: isDark ? "rgba(33, 150, 243, 0.18)" : "#e3f2fd",
      color: isDark ? "#64b5f6" : "#1565c0",
    },
    chipVencendo: {
      ...chipBase,
      backgroundColor: isDark ? "rgba(255, 193, 7, 0.18)" : "#fff8e1",
      color: isDark ? "#ffd54f" : "#f57f17",
    },
    chipVencido: {
      ...chipBase,
      backgroundColor: isDark ? "rgba(244, 67, 54, 0.18)" : "#ffebee",
      color: isDark ? "#e57373" : "#c62828",
    },

    // Cards mobile
    card: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(1.5),
      borderRadius: theme.spacing(1),
      border: `1px solid ${theme.palette.divider}`,
    },
    cardSelected: {
      borderColor: theme.palette.primary.main,
      boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
    },
    cardHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: theme.spacing(1),
    },
    cardName: {
      fontWeight: 600,
      fontSize: "0.95rem",
    },
    cardRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: theme.spacing(0.5),
    },
    cardLabel: {
      fontSize: "0.75rem",
      color: theme.palette.text.secondary,
      fontWeight: 500,
    },
    cardValue: {
      fontSize: "0.82rem",
    },
    cardChips: {
      display: "flex",
      gap: theme.spacing(0.5),
      flexWrap: "wrap",
      marginTop: theme.spacing(1),
    },

    // Seção do formulário
    formSectionTitle: {
      fontWeight: 600,
      fontSize: "0.8rem",
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(1.5),
    },

    // Cabeçalho da lista: título, resumo, busca e filtros
    listHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1),
      marginTop: theme.spacing(1),
    },
    listTitle: {
      fontWeight: 600,
      fontSize: "0.95rem",
    },
    companyCount: {
      fontSize: "0.8rem",
      color: theme.palette.text.secondary,
      fontWeight: 500,
    },
    filtersRow: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1.5),
    },
    searchInput: {
      minWidth: 220,
      [theme.breakpoints.down("xs")]: {
        width: "100%",
      },
    },
    filterChip: {
      fontSize: "0.75rem",
      fontWeight: 500,
      height: 26,
    },

    // Estado vazio
    emptyState: {
      textAlign: "center",
      padding: theme.spacing(5),
      color: theme.palette.text.secondary,
    },
    emptyStateIcon: {
      fontSize: 42,
      opacity: 0.4,
      marginBottom: theme.spacing(1),
    },
  };
});

// --- Helpers de status de vencimento ---

// Retorna "expired", "expiring" (vence em até 5 dias) ou null.
const getDueStatus = (dueDate) => {
  if (!dueDate || !moment(dueDate).isValid()) return null;
  const diff = moment(dueDate).startOf("day").diff(moment().startOf("day"), "days");
  if (diff < 0) return "expired";
  if (diff <= 5) return "expiring";
  return null;
};

const dueDateRelativeText = (dueDate) => {
  if (!dueDate || !moment(dueDate).isValid()) return "";
  const diff = moment(dueDate).startOf("day").diff(moment().startOf("day"), "days");
  if (diff < -1) return `Venceu há ${Math.abs(diff)} dias`;
  if (diff === -1) return "Venceu ontem";
  if (diff === 0) return "Vence hoje";
  if (diff === 1) return "Vence amanhã";
  return `Vence em ${diff} dias`;
};

const dueDateChip = (classes, dueDate) => {
  const status = getDueStatus(dueDate);
  if (!status) return null;
  const chip =
    status === "expired" ? (
      <Chip size="small" label="Vencido" className={classes.chipVencido} icon={<CancelIcon style={{ fontSize: 12 }} />} />
    ) : (
      <Chip size="small" label="Vencendo" className={classes.chipVencendo} icon={<WarningIcon style={{ fontSize: 12 }} />} />
    );
  return (
    <Tooltip title={dueDateRelativeText(dueDate)}>
      <span>{chip}</span>
    </Tooltip>
  );
};

// --- Validação do formulário ---

const CompanySchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .min(2, "Nome precisa ter ao menos 2 caracteres")
    .max(100, "Nome não pode passar de 100 caracteres")
    .required("Nome é obrigatório"),
  email: Yup.string()
    .email("E-mail inválido")
    .required("E-mail é obrigatório"),
  planId: Yup.number()
    .typeError("Selecione um plano")
    .required("Selecione um plano"),
});

const RECURRENCE_OPTIONS = [
  { value: "MENSAL", label: "Mensal" },
  { value: "BIMESTRAL", label: "Bimestral" },
  { value: "TRIMESTRAL", label: "Trimestral" },
  { value: "SEMESTRAL", label: "Semestral" },
  { value: "ANUAL", label: "Anual" },
];

const RECURRENCE_INCREMENTS = {
  MENSAL: [1, "month"],
  BIMESTRAL: [2, "month"],
  TRIMESTRAL: [3, "month"],
  SEMESTRAL: [6, "month"],
  ANUAL: [12, "month"],
};

// --- CompanyForm ---

export function CompanyForm(props) {
  const { onSubmit, onDelete, onCancel, initialValue, loading } = props;
  const classes = useStyles();
  const [plans, setPlans] = useState([]);
  const [modalUser, setModalUser] = useState(false);
  const [firstUser, setFirstUser] = useState({});

  const [record, setRecord] = useState({
    name: "",
    email: "",
    phone: "",
    planId: "",
    status: true,
    campaignsEnabled: false,
    dueDate: "",
    recurrence: "",
    ...initialValue,
  });

  const { list: listPlans } = usePlans();

  useEffect(() => {
    async function fetchData() {
      try {
        const list = await listPlans();
        setPlans(list || []);
      } catch (e) {
        toastError(e);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Normaliza o vencimento para o formato aceito pelo <input type="date">.
    // Sem isso, a data ISO completa vinda da API deixa o campo em branco.
    const normalized = { ...initialValue };
    if (normalized.dueDate && moment(normalized.dueDate).isValid()) {
      normalized.dueDate = moment(normalized.dueDate).format("YYYY-MM-DD");
    } else {
      normalized.dueDate = "";
    }
    setRecord((prev) => ({ ...prev, ...normalized }));
  }, [initialValue]);

  const handleSubmit = async (data) => {
    const payload = { ...data };
    if (payload.dueDate === "" || moment(payload.dueDate).isValid() === false) {
      payload.dueDate = null;
    }
    onSubmit(payload);
  };

  const handleOpenModalUsers = async () => {
    try {
      const { data } = await api.get("/users/list", {
        params: { companyId: initialValue.id },
      });
      if (isArray(data) && data.length) {
        setFirstUser(head(data));
      }
      setModalUser(true);
    } catch (e) {
      toastError(e);
    }
  };

  const handleCloseModalUsers = () => {
    setFirstUser({});
    setModalUser(false);
  };

  const isEditing = record.id !== undefined;

  return (
    <>
      <ModalUsers
        userId={firstUser.id}
        companyId={initialValue.id}
        open={modalUser}
        onClose={handleCloseModalUsers}
      />
      <Formik
        enableReinitialize
        initialValues={record}
        validationSchema={CompanySchema}
        onSubmit={(values, { setSubmitting }) => {
          handleSubmit(values);
          setSubmitting(false);
        }}
      >
        {({ values, setFieldValue, touched, errors }) => {
          const incrementDueDate = () => {
            const inc = RECURRENCE_INCREMENTS[values.recurrence];
            if (!values.dueDate || !moment(values.dueDate).isValid()) {
              toast.warn("Preencha o vencimento antes de renovar.");
              return;
            }
            if (!inc) {
              toast.warn("Selecione a recorrência antes de renovar.");
              return;
            }
            const newDate = moment(values.dueDate).add(...inc).format("YYYY-MM-DD");
            setFieldValue("dueDate", newDate);
            toast.info(
              `Novo vencimento: ${moment(newDate).format("DD/MM/YYYY")}. Clique em "Salvar alterações" para confirmar.`
            );
          };

          return (
            <Form className={classes.fullWidth}>

              {/* Dados da empresa */}
              <Typography className={classes.formSectionTitle}>
                {isEditing ? `Editar empresa — ${record.name || ""}` : "Nova empresa"}
              </Typography>
              <Grid spacing={2} container>
                <Grid xs={12} sm={6} md={4} item>
                  <Field
                    as={TextField}
                    label="Nome"
                    name="name"
                    variant="outlined"
                    className={classes.fullWidth}
                    margin="dense"
                    required
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                  />
                </Grid>
                <Grid xs={12} sm={6} md={3} item>
                  <Field
                    as={TextField}
                    label="E-mail"
                    name="email"
                    variant="outlined"
                    className={classes.fullWidth}
                    margin="dense"
                    required
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                  />
                </Grid>
                <Grid xs={12} sm={6} md={2} item>
                  <Field as={TextField} label="Telefone" name="phone" variant="outlined" className={classes.fullWidth} margin="dense" />
                </Grid>
                <Grid xs={12} sm={6} md={3} item>
                  <FormControl
                    margin="dense"
                    variant="outlined"
                    fullWidth
                    required
                    error={touched.planId && Boolean(errors.planId)}
                  >
                    <InputLabel htmlFor="plan-selection">Plano</InputLabel>
                    <Field as={Select} id="plan-selection" label="Plano" labelId="plan-selection-label" name="planId">
                      {plans.map((plan) => (
                        <MenuItem key={plan.id} value={plan.id}>{plan.name}</MenuItem>
                      ))}
                    </Field>
                    {touched.planId && errors.planId && (
                      <FormHelperText>{errors.planId}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {/* Configurações */}
                <Grid xs={12} sm={6} md={2} item>
                  <FormControl margin="dense" variant="outlined" fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Field as={Select} label="Status" name="status">
                      <MenuItem value={true}>Ativo</MenuItem>
                      <MenuItem value={false}>Inativo</MenuItem>
                    </Field>
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={6} md={2} item>
                  <FormControl margin="dense" variant="outlined" fullWidth>
                    <InputLabel>Campanhas</InputLabel>
                    <Field as={Select} label="Campanhas" name="campaignsEnabled">
                      <MenuItem value={true}>Habilitadas</MenuItem>
                      <MenuItem value={false}>Desabilitadas</MenuItem>
                    </Field>
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={6} md={2} item>
                  <Field
                    as={TextField}
                    label="Vencimento"
                    type="date"
                    name="dueDate"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    fullWidth
                    margin="dense"
                  />
                </Grid>
                <Grid xs={12} sm={6} md={2} item>
                  <FormControl margin="dense" variant="outlined" fullWidth>
                    <InputLabel>Recorrência</InputLabel>
                    <Field as={Select} label="Recorrência" name="recurrence">
                      {RECURRENCE_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Field>
                  </FormControl>
                </Grid>

                {/* Ações */}
                <Grid xs={12} item>
                  <Box display="flex" justifyContent="flex-end" flexWrap="wrap" style={{ gap: 8, paddingTop: 8 }}>
                    {isEditing && (
                      <Button
                        onClick={() => onDelete(record)}
                        variant="outlined"
                        color="secondary"
                        size="small"
                        style={{ marginRight: "auto" }}
                      >
                        Excluir
                      </Button>
                    )}
                    <Button onClick={() => onCancel()} variant="outlined" size="small">
                      {isEditing ? "Cancelar edição" : "Limpar"}
                    </Button>
                    {isEditing && (
                      <>
                        <Button onClick={incrementDueDate} variant="outlined" color="primary" size="small">
                          + Vencimento
                        </Button>
                        <Button onClick={handleOpenModalUsers} variant="outlined" color="primary" size="small">
                          Ver usuário
                        </Button>
                      </>
                    )}
                    <ButtonWithSpinner loading={loading} type="submit" variant="contained" color="primary" size="small">
                      {isEditing ? "Salvar alterações" : "Cadastrar"}
                    </ButtonWithSpinner>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </>
  );
}

// --- Diálogo de exclusão com confirmação pelo nome ---

function DeleteCompanyDialog({ open, company, loading, onClose, onConfirm }) {
  const [confirmName, setConfirmName] = useState("");

  useEffect(() => {
    if (open) setConfirmName("");
  }, [open]);

  const nameMatches =
    confirmName.trim().toLowerCase() === (company?.name || "").trim().toLowerCase();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Excluir empresa</DialogTitle>
      <DialogContent dividers>
        <Typography gutterBottom>
          Você está prestes a excluir a empresa{" "}
          <strong>{company?.name || ""}</strong>. Todos os dados vinculados a
          ela (usuários, atendimentos, conexões e configurações) serão
          perdidos. <strong>Esta ação não pode ser desfeita.</strong>
        </Typography>
        <TextField
          autoFocus
          fullWidth
          variant="outlined"
          margin="dense"
          label={`Digite "${company?.name || ""}" para confirmar`}
          value={confirmName}
          onChange={(e) => setConfirmName(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="default">
          Cancelar
        </Button>
        <ButtonWithSpinner
          loading={loading}
          onClick={onConfirm}
          variant="contained"
          color="secondary"
          disabled={!nameMatches || loading}
        >
          Excluir definitivamente
        </ButtonWithSpinner>
      </DialogActions>
    </Dialog>
  );
}

// --- Grid (tabela desktop + cards mobile) ---

export function CompaniesManagerGrid(props) {
  const { records, onSelect, selectedId, loading } = props;
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { dateToClient } = useDate();

  const renderCampaignsStatus = (row) => {
    if (has(row, "settings") && isArray(row.settings) && row.settings.length > 0) {
      const setting = row.settings.find((s) => s.key === "campaignsEnabled");
      if (setting) return setting.value === "true";
    }
    return false;
  };

  const emptyState = (
    <div className={classes.emptyState}>
      <BusinessIcon className={classes.emptyStateIcon} />
      <Typography variant="body2">Nenhuma empresa encontrada.</Typography>
      <Typography variant="caption" color="textSecondary">
        Ajuste a busca ou os filtros, ou cadastre uma nova empresa acima.
      </Typography>
    </div>
  );

  // --- Visualização mobile: cards ---
  if (isMobile) {
    if (loading) {
      return (
        <Box>
          {[0, 1, 2].map((i) => (
            <Paper key={i} className={classes.card} elevation={0}>
              <Skeleton variant="text" width="60%" height={24} />
              <Skeleton variant="text" width="40%" />
              <Skeleton variant="rect" height={60} style={{ marginTop: 8, borderRadius: 4 }} />
            </Paper>
          ))}
        </Box>
      );
    }
    if (!records.length) {
      return <Paper elevation={0} variant="outlined">{emptyState}</Paper>;
    }
    return (
      <Box>
        {records.map((row) => (
          <Paper
            key={row.id}
            className={`${classes.card} ${row.id === selectedId ? classes.cardSelected : ""}`}
            elevation={0}
          >
            <div className={classes.cardHeader}>
              <div>
                <Typography className={classes.cardName}>{row.name || "-"}</Typography>
                <Typography variant="caption" color="textSecondary">{row.email || "-"}</Typography>
              </div>
              <Tooltip title="Editar">
                <IconButton size="small" aria-label="Editar empresa" onClick={() => onSelect(row)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>

            <Divider style={{ marginBottom: 8 }} />

            <div className={classes.cardRow}>
              <Typography className={classes.cardLabel}>Telefone</Typography>
              <Typography className={classes.cardValue}>{row.phone || "-"}</Typography>
            </div>
            <div className={classes.cardRow}>
              <Typography className={classes.cardLabel}>Plano</Typography>
              <Typography className={classes.cardValue}>{row.planId !== null ? row.plan?.name : "-"}</Typography>
            </div>
            <div className={classes.cardRow}>
              <Typography className={classes.cardLabel}>Criada em</Typography>
              <Typography className={classes.cardValue}>{dateToClient(row.createdAt)}</Typography>
            </div>
            <div className={classes.cardRow}>
              <Typography className={classes.cardLabel}>Vencimento</Typography>
              <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                <Typography className={classes.cardValue}>{dateToClient(row.dueDate)}</Typography>
                {dueDateChip(classes, row.dueDate)}
              </Box>
            </div>

            <div className={classes.cardChips}>
              <Chip
                size="small"
                label={row.status === false ? "Inativo" : "Ativo"}
                className={row.status === false ? classes.chipInativo : classes.chipAtivo}
              />
              {renderCampaignsStatus(row) && (
                <Chip size="small" label="Campanhas" className={classes.chipCampanha} />
              )}
            </div>
          </Paper>
        ))}
      </Box>
    );
  }

  // --- Visualização desktop: tabela ---
  return (
    <Paper className={classes.tableContainer} elevation={0} variant="outlined">
      <Table size="small">
        <TableHead className={classes.tableHead}>
          <TableRow>
            <TableCell style={{ width: 40 }} />
            <TableCell>Nome</TableCell>
            <TableCell>E-mail</TableCell>
            <TableCell>Telefone</TableCell>
            <TableCell>Plano</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Campanhas</TableCell>
            <TableCell>Criada em</TableCell>
            <TableCell>Vencimento</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading &&
            [0, 1, 2, 3].map((i) => (
              <TableRow key={`skeleton-${i}`}>
                {Array.from({ length: 9 }).map((_, j) => (
                  <TableCell key={j}>
                    <Skeleton variant="text" />
                  </TableCell>
                ))}
              </TableRow>
            ))}

          {!loading && !records.length && (
            <TableRow>
              <TableCell colSpan={9}>{emptyState}</TableCell>
            </TableRow>
          )}

          {!loading &&
            records.map((row) => (
              <TableRow
                key={row.id}
                className={`${classes.tableRow} ${row.id === selectedId ? classes.tableRowSelected : ""}`}
              >
                <TableCell>
                  <Tooltip title="Editar empresa">
                    <IconButton size="small" aria-label="Editar empresa" onClick={() => onSelect(row)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <BusinessIcon fontSize="small" color="action" />
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {row.name || "-"}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">{row.email || "-"}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">{row.phone || "-"}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{row.planId !== null ? row.plan?.name : "-"}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.status === false ? "Inativo" : "Ativo"}
                    className={row.status === false ? classes.chipInativo : classes.chipAtivo}
                    icon={row.status === false
                      ? <CancelIcon style={{ fontSize: 12 }} />
                      : <CheckCircleIcon style={{ fontSize: 12 }} />
                    }
                  />
                </TableCell>
                <TableCell>
                  {renderCampaignsStatus(row) ? (
                    <Chip size="small" label="Habilitadas" className={classes.chipCampanha} />
                  ) : (
                    <Typography variant="body2" color="textSecondary">—</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">{dateToClient(row.createdAt)}</Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                    <Typography variant="body2">{dateToClient(row.dueDate)}</Typography>
                    {dueDateChip(classes, row.dueDate)}
                  </Box>
                  {row.recurrence && (
                    <Typography variant="caption" color="textSecondary">{row.recurrence}</Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

// --- CompaniesManager principal ---

const EMPTY_RECORD = {
  name: "",
  email: "",
  phone: "",
  planId: "",
  status: true,
  campaignsEnabled: false,
  dueDate: "",
  recurrence: "",
};

const STATUS_FILTERS = [
  { value: "all", label: "Todas" },
  { value: "active", label: "Ativas" },
  { value: "inactive", label: "Inativas" },
  { value: "expiring", label: "Vencendo" },
  { value: "expired", label: "Vencidas" },
];

export default function CompaniesManager() {
  const classes = useStyles();
  const { list, save, update, remove } = useCompanies();
  const formRef = useRef(null);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [record, setRecord] = useState(EMPTY_RECORD);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCompanies = async () => {
    setListLoading(true);
    try {
      const companyList = await list();
      setRecords(isArray(companyList) ? companyList : []);
    } catch (e) {
      toastError(e);
    }
    setListLoading(false);
  };

  const handleSubmit = async (data) => {
    setLoading(true);
    try {
      if (data.id !== undefined) {
        await update(data);
      } else {
        await save(data);
      }
      await loadCompanies();
      handleCancel();
      toast.success("Operação realizada com sucesso!");
    } catch (e) {
      toastError(e);
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await remove(record.id);
      setShowConfirmDialog(false);
      await loadCompanies();
      handleCancel();
      toast.success("Empresa excluída com sucesso!");
    } catch (e) {
      toastError(e);
    }
    setLoading(false);
  };

  const handleCancel = () => {
    setRecord(EMPTY_RECORD);
  };

  const handleSelect = (data) => {
    let campaignsEnabled = false;
    const setting = (data.settings || []).find((s) => s.key.indexOf("campaignsEnabled") > -1);
    if (setting) {
      campaignsEnabled = setting.value === "true" || setting.value === "enabled";
    }

    setRecord({
      id: data.id,
      name: data.name || "",
      phone: data.phone || "",
      email: data.email || "",
      planId: data.planId || "",
      status: data.status === false ? false : true,
      campaignsEnabled,
      dueDate: data.dueDate || "",
      recurrence: data.recurrence || "",
    });

    // Deixa claro que o formulário acima entrou em modo edição.
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Estatísticas para o resumo e para os contadores dos filtros
  const stats = useMemo(() => {
    const result = { all: records.length, active: 0, inactive: 0, expiring: 0, expired: 0 };
    records.forEach((row) => {
      if (row.status === false) result.inactive += 1;
      else result.active += 1;
      const due = getDueStatus(row.dueDate);
      if (due === "expired") result.expired += 1;
      if (due === "expiring") result.expiring += 1;
    });
    return result;
  }, [records]);

  const filteredRecords = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return records.filter((row) => {
      if (statusFilter === "active" && row.status === false) return false;
      if (statusFilter === "inactive" && row.status !== false) return false;
      if (statusFilter === "expiring" && getDueStatus(row.dueDate) !== "expiring") return false;
      if (statusFilter === "expired" && getDueStatus(row.dueDate) !== "expired") return false;
      if (!term) return true;
      return (
        (row.name || "").toLowerCase().includes(term) ||
        (row.email || "").toLowerCase().includes(term) ||
        (row.phone || "").toLowerCase().includes(term)
      );
    });
  }, [records, searchTerm, statusFilter]);

  const summaryText = useMemo(() => {
    const parts = [`${stats.all} ${stats.all === 1 ? "empresa" : "empresas"}`];
    if (stats.expired) parts.push(`${stats.expired} vencida${stats.expired > 1 ? "s" : ""}`);
    if (stats.expiring) parts.push(`${stats.expiring} vencendo`);
    return parts.join(" · ");
  }, [stats]);

  return (
    <Paper className={classes.mainPaper} elevation={0}>
      <Grid spacing={2} container>
        <Grid xs={12} item>
          <div ref={formRef}>
            <CompanyForm
              initialValue={record}
              onDelete={() => setShowConfirmDialog(true)}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              loading={loading}
            />
          </div>
        </Grid>

        <Grid xs={12} item>
          <Divider style={{ marginBottom: 8 }} />
          <Box className={classes.listHeader}>
            <Typography className={classes.listTitle}>Empresas cadastradas</Typography>
            <Typography className={classes.companyCount}>{summaryText}</Typography>
          </Box>

          <Box className={classes.filtersRow}>
            <TextField
              size="small"
              variant="outlined"
              placeholder="Buscar por nome, e-mail ou telefone…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={classes.searchInput}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="disabled" />
                  </InputAdornment>
                ),
              }}
            />
            {STATUS_FILTERS.map((filter) => (
              <Chip
                key={filter.value}
                size="small"
                clickable
                className={classes.filterChip}
                label={`${filter.label} (${stats[filter.value]})`}
                color={statusFilter === filter.value ? "primary" : "default"}
                variant={statusFilter === filter.value ? "default" : "outlined"}
                onClick={() => setStatusFilter(filter.value)}
              />
            ))}
          </Box>

          <CompaniesManagerGrid
            records={filteredRecords}
            onSelect={handleSelect}
            selectedId={record.id}
            loading={listLoading}
          />
        </Grid>
      </Grid>

      <DeleteCompanyDialog
        open={showConfirmDialog}
        company={record}
        loading={loading}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleDelete}
      />
    </Paper>
  );
}
